import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { SortDirection } from '@ddt-web/common';
import { ConfigService, InjectConfig, PaConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { PartnerPlatformControlsList, PortfolioFlagsEnum, WebUserDataConstants } from '@ddt-web/domain';
import { PortfolioUtil } from '@ddt-web/portfolio-data';
import * as chunk from 'lodash/chunk';
import * as groupBy from 'lodash/groupBy';

import { HeaderType, TableHeader } from './portfolio-header.config';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'portfolio-table',
	templateUrl: './portfolio-table.component.html',
	styleUrls: ['./portfolio-table.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class PortfolioTableComponent implements OnInit, OnChanges {
	@InjectConfig(PaConfigKey.SHOW_IMPORT_PARTNER_PORTFOLIO) public readonly showImportPartnerPortfolio: boolean = false;
	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) private readonly partnerPlatformControlsList: PartnerPlatformControlsList;

	@Input() public maxLimit: number = 3;
	@Input() public maxChildrenLimit: number;
	@Input() public warningFlags: PortfolioFlagsEnum[] = [PortfolioFlagsEnum.LIST_OF_HOLDINGS, PortfolioFlagsEnum.NO_WEIGTHS];
	@Input() public errorFlags: PortfolioFlagsEnum[] = [PortfolioFlagsEnum.NO_HOLDINGS];
	@Input() public hideLastModifiedDate: boolean = false;
	@Input() public searchPlaceholder: string = 'Filter list by keyword';
	@Input() public enableFilterByPortfolioType: boolean = false;
	@Input() public groupSelectedPortfoliosByType: boolean = true;
	@Input() public isPartiallyLoading: boolean = false;
	@Input() public partialLoadingMessage: string;
	@Input() public showCreateNewPortfolioButton?: boolean = false;
	@Input() public enableDeselectAll: boolean = false;
	@Input() public showDeselectAllWithPills: boolean = false;
	@Input() public disabledPortfolios: Array<any> = [];
	@Input() public enablePagination: boolean = false;
	@Input() public pageSize: number = 5;
	@Input() public currentAnalyzedPartner: string;
	@Output() public iconClick: EventEmitter<any> = new EventEmitter();
	@Output() public isValid: EventEmitter<boolean> = new EventEmitter();
	@Output() public isPortfolioEditorVisible: EventEmitter<boolean> = new EventEmitter();
	@Output() public isMaxChildLimitReached: EventEmitter<boolean> = new EventEmitter();
	@Output() public onPortfolioSelected: EventEmitter<any> = new EventEmitter();

	public groupedSelectedPortfolios: Array<any> = new Array<any>();
	public groupedPortfolios: Array<any> = new Array<any>();
	public portfolioGroupsToFilterBy: Array<any> = new Array<any>();
	public filteredPortfolios: Array<any> = new Array<any>();
	public portfolioPages: Array<Array<any>> = new Array<Array<any>>();
	public dateFormat: string;
	public searchInput = new UntypedFormControl();
	public isSearchInputFocused: boolean = false;
	public isPortfolioTypeMenuOpen: boolean = false;
	public selectedPageIndex: number = 0;

	public PortfolioUtil = PortfolioUtil;

	public isRowDisabledByUniqueId = new Map<string, boolean>();

	public headers: Array<TableHeader> = new Array<TableHeader>();
	public SortDirection = SortDirection;
	private _selectedPortfolios: Array<any> = new Array<any>();

	private _allPortfolios: Array<any> = new Array<any>();

	constructor(private configService: ConfigService) { }

	@Input()
	public set allPortfolios(allPortfolios: Array<any>) {
		if (!this.showImportPartnerPortfolio) {
			this._allPortfolios = allPortfolios.filter(portfolio => portfolio.subType !== WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO);
		} else {
			// Filter out partner portfolios for which showSavedPortfolios config is false
			this._allPortfolios = allPortfolios
				.filter(portfolio => {
					const originPlatformId = portfolio?.originPlatform?.platform;
					return !(portfolio.subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO && !this.partnerPlatformControlsList[originPlatformId]?.showSavedPortfolios);
				});
		}
		if (!this.searchInput.value) {
			this.filteredPortfolios = this._allPortfolios;
		}
		this.filterInvalidPortfolios();
		this.groupPortfolios();
	}

	public get allPortfolios(): Array<any> {
		return this._allPortfolios;
	}

	public ngOnInit(): void {
		this.initializeHeaders();

		this.filteredPortfolios = this.allPortfolios;
		this.sortPortfoliosBy(this.headers[1]);
		this.groupPortfolios();
		this.selectAllPortfolioTypes();
		this.dateFormat = this.configService.get(SharedConfigKey.DATE_FORMAT, 'mediumDate');
		this.isMaxChildLimitReached.emit(this.isChildLimitReached());
		this.portfolioPages = chunk(this.filteredPortfolios, this.pageSize);
		this.selectedPageIndex = 0;
		this.updateDisabledPortfolios();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.groupPortfolios();
		this.sortPortfoliosBy();
		this.selectAllPortfolioTypes();

		this.updateDisabledPortfolios();
	}

	public iconClicked(portfolioIssue: [any, boolean]): void {
		this.iconClick.emit(portfolioIssue);
	}

	public hasWarnings(portfolio: any): boolean {
		for (const warnFlag of this.warningFlags) {
			if (portfolio.flags && portfolio.flags.length && portfolio.flags.indexOf(warnFlag) > -1) {
				return true;
			}
		}
		return false;
	}

	public hasErrors(portfolio: any): boolean {
		for (const errorFlag of this.errorFlags) {
			if (portfolio.flags && portfolio.flags.length && portfolio.flags.indexOf(errorFlag) > -1) {
				return true;
			}
		}
		return false;
	}

	public get selectedPortfolios(): Array<any> {
		return this._selectedPortfolios;
	}

	@Input()
	public set selectedPortfolios(ports: Array<any>) {
		this._selectedPortfolios = ports;
		this.groupPortfolios();
		this.isValid.emit(this.isSelectionValid());
	}

	/**
	 * Updates the _allPortfolios by filtering out the invalid portfolios (no description and invalid subType)
	 *
	 * @private
	 */
	private filterInvalidPortfolios(): void {
		this._allPortfolios = this._allPortfolios.filter(portfolio => PortfolioUtil.isValidPortfolioName(portfolio));
	}

	private initializeHeaders(): void {
		const nameHeader: TableHeader = new TableHeader('Portfolio Name', HeaderType.NAME, true, SortDirection.UNSORTED);
		const dateHeader: TableHeader = new TableHeader('Last Modified', HeaderType.LAST_MODIFIED, true, SortDirection.ASC);
		const lastModifiedHeader: TableHeader = new TableHeader('Portfolio Type', HeaderType.TYPE, true, SortDirection.UNSORTED);

		this.headers = [nameHeader, dateHeader, lastModifiedHeader];
		if (this.hideLastModifiedDate) {
			this.headers = [nameHeader, lastModifiedHeader];
		}
	}

	/**
	 * Event triggered by:
	 * - the 'x' icon click to deselect a portfolio
	 * - the check box clicked on the table
	 */
	public onPortfolioSelectionClick(clickedPortfolio: any): void {
		const position: number = this._selectedPortfolios.findIndex(p => (p.uniqueId || p.getUniqueId()) === (clickedPortfolio.uniqueId || clickedPortfolio.getUniqueId()));

		if (position > -1) {
			// Remove portfolio
			this._selectedPortfolios.splice(position, 1);
		} else {
			// Add portfolio
			this._selectedPortfolios.push(clickedPortfolio);
		}
		this.groupPortfolios();
		this.isValid.emit(this.isSelectionValid());
		this.isMaxChildLimitReached.emit(this.isChildLimitReached());
		this.onPortfolioSelected.emit(clickedPortfolio);
		this.updateDisabledPortfolios();
	}

	private isSelectionValid(): boolean {
		for (const port of this._selectedPortfolios) {
			if (this.hasErrors(port)) {
				return false;
			}
		}
		return true;
	}

	private groupPortfolios(): void {
		const allPortfoliosGroupedByType = groupBy(this._allPortfolios, 'subType');
		this.groupedPortfolios = Object.keys(allPortfoliosGroupedByType);

		if (this.groupSelectedPortfoliosByType) {
			const selectedPortfoliosGroupedByType = groupBy(this._selectedPortfolios, 'subType');
			this.groupedSelectedPortfolios = new Array<any>();

			for (const group in selectedPortfoliosGroupedByType) {
				if (selectedPortfoliosGroupedByType[group]) {
					const groupOfPortfolios: any[] = [];
					selectedPortfoliosGroupedByType[group].forEach(element => groupOfPortfolios.push(element));
					this.groupedSelectedPortfolios.push(groupOfPortfolios);
				}
			}
		} else {
			this.groupedSelectedPortfolios = [this._selectedPortfolios];
		}
	}

	public setSearchInputFocused(): void {
		this.isSearchInputFocused = true;
	}

	private isPortfolioLimitReached(): boolean {
		if (this._selectedPortfolios && this._selectedPortfolios.length) {
			return this._selectedPortfolios.length >= this.maxLimit;
		} else {
			return false;
		}
	}

	private isChildLimitReached(): boolean {
		if (this._selectedPortfolios && this._selectedPortfolios.length && this.maxChildrenLimit) {
			const childCount = this._selectedPortfolios.reduce((acc, val) => acc + val.childCount, 0);
			const maxChildrenReached = childCount > this.maxChildrenLimit;
			return maxChildrenReached;
		} else {
			return false;
		}
	}

	private isPortfolioSelected(portfolio: any): boolean {
		return this._selectedPortfolios.findIndex(p => (p.uniqueId || p.getUniqueId()) === (portfolio.uniqueId || portfolio.getUniqueId())) > -1;
	}


	public getPortfoliosByKeyFromKeyboardEvent(event: any) {
		const typedValueLowCase = event.target?.value.toLowerCase();
		this.getPortfoliosByKey(typedValueLowCase);
	}

	private getPortfoliosByKey(typedValueLowCase: string): void {
		this.filteredPortfolios = this.allPortfolios.filter(portfolio => {
			const portfolioName = PortfolioUtil.getPortfolioName(portfolio);
			const portfolioNameLowCase = portfolioName.toLowerCase();
			return this.portfolioGroupsToFilterBy.includes(portfolio.subType) && portfolioNameLowCase.indexOf(typedValueLowCase) !== -1;
		});

		if (this.enablePagination) {
			this.portfolioPages = chunk(this.filteredPortfolios, this.pageSize);
		}
	}

	private selectAllPortfolioTypes(): void {
		this.portfolioGroupsToFilterBy = [...this.groupedPortfolios];
	}

	private selectNoPortfolioTypes(): void {
		this.portfolioGroupsToFilterBy = [];
	}

	public filterByPortfolioType(event: any, groupName: string): void {
		if (event.checked) {
			this.portfolioGroupsToFilterBy.push(groupName);
		} else {
			const index = this.portfolioGroupsToFilterBy.findIndex(group => group === groupName);
			this.portfolioGroupsToFilterBy.splice(index, 1);
		}

		// We're passing the value of the text search as the value (using #searchText.value)
		// so we can pass it along to the filter function and the text search will be preserved
		this.getPortfoliosByKey(event.source?.value.toLowerCase());
	}

	public toggleAllPortfolioTypes(event: any): void {
		if (event.checked) {
			this.selectAllPortfolioTypes();
		} else {
			this.selectNoPortfolioTypes();
		}

		this.getPortfoliosByKey(event.source?.value.toLowerCase());
	}

	public deselectAllPortfolios(): void {
		this._selectedPortfolios.length = 0;
		this.groupedSelectedPortfolios = new Array<any>();
		this.updateDisabledPortfolios();
	}

	public isFilteredPortfoliosEmpty(): boolean {
		return !this.filteredPortfolios || this.filteredPortfolios.length === 0;
	}

	private updateDisabledPortfolios(): void {
		if (this.showImportPartnerPortfolio) { // this is to ensure the other app's disabledPortfolios won't get affected
			const currentPlatform = this.currentAnalyzedPartner ?
				this.currentAnalyzedPartner :
				this.selectedPortfolios?.find(portfolio => portfolio.subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO)?.originPlatform.platform;
			if (currentPlatform) {
				this.disabledPortfolios = this.allPortfolios.filter(portfolio =>
					portfolio.subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO && portfolio.originPlatform.platform !== currentPlatform);
			} else {
				this.disabledPortfolios = [];
			}
		}

		this.isRowDisabledByUniqueId = this.buildIsRowDisabledByUniqueId();
	}

	private buildIsRowDisabledByUniqueId(): Map<string, boolean> {
		const disabledPortfolioIds = this.disabledPortfolios.map(portfolio => portfolio.uniqueId);

		return this.filteredPortfolios.reduce((isRowDisabledByUniqueId, portfolio) => {
			const isPortfolioDeselected = !this.isPortfolioSelected(portfolio);
			const isPortfolioLimitReached = this.isPortfolioLimitReached();
			const isChildLimitReached = this.isChildLimitReached();
			const isIncluded = !!disabledPortfolioIds.includes(portfolio.uniqueId);

			const isDisabled = (isPortfolioDeselected && (isPortfolioLimitReached || isChildLimitReached)) || isIncluded;
			isRowDisabledByUniqueId.set(portfolio.uniqueId, isDisabled);

			return isRowDisabledByUniqueId;
		}, new Map());
	}

	/**
	 * Sort Helpers
	 */

	private sortPortfoliosBy(header?: TableHeader): void {
		let toggleSortOrder = true;
		if (!header) {
			toggleSortOrder = false;
			header = this.headers.find(h => h.getSortingDirection() !== SortDirection.UNSORTED);
			if (!header) {
				return;
			}
		}

		if (toggleSortOrder) {
			this.resetHeadersSortingDirections(header);
		}

		switch (header.getType()) {
			case HeaderType.NAME: {
				this.filteredPortfolios.sort((portfolioOne: any, portfolioTwo: any) => {
					const nameOne: string = PortfolioUtil.getPortfolioName(portfolioOne);
					const nameTwo: string = PortfolioUtil.getPortfolioName(portfolioTwo);
					const val: number = nameOne.toLocaleLowerCase().localeCompare(nameTwo.toLocaleLowerCase()) * header.getSortingDirection();
					return val;
				});
				break;
			}
			case HeaderType.LAST_MODIFIED: {
				this.filteredPortfolios.sort((portfolioOne: any, portfolioTwo: any) => {
					const portfolioOneLastModifiedDate = PortfolioUtil.getPortfolioLastModifiedDate(portfolioOne);
					const portfolioTwoLastModifiedDate = PortfolioUtil.getPortfolioLastModifiedDate(portfolioTwo);

					// temporarily, until we get the date back from envestnet
					// if portfolio does not have last modified date, show it first in the list
					if (portfolioOneLastModifiedDate === null) {
						return -1;
					}

					if (portfolioTwoLastModifiedDate === null) {
						return 1;
					}

					return header.getSortingDirection() * (portfolioOneLastModifiedDate - portfolioTwoLastModifiedDate);
				});

				break;
			}
			case HeaderType.TYPE: {
				this.filteredPortfolios.sort((portfolioOne: any, portfolioTwo: any) => {
					const subTypeOne: string = PortfolioUtil.getPortfolioSubTypeString(portfolioOne).toLocaleLowerCase();
					const subTypeTwo: string = PortfolioUtil.getPortfolioSubTypeString(portfolioTwo).toLocaleLowerCase();
					return header.getSortingDirection() * subTypeOne.localeCompare(subTypeTwo);
				});
				break;
			}
		}
	}

	private resetHeadersSortingDirections(header: TableHeader): void {
		header.nextSortingDirection();
		this.headers
			.filter((currentHeader: TableHeader) => currentHeader !== header)
			.forEach((currentHeader: TableHeader) => {
				currentHeader.setSortingDirection(SortDirection.UNSORTED);
			});
	}

	@HostListener('click', ['$event.target'])
	public onClick(target): void {
		if (target.closest('.portfolio-types') === null) {
			this.isPortfolioTypeMenuOpen = false;
		}
	}

	public openCreateNewPortfolio(): void {
		this.isPortfolioEditorVisible.emit(true);
	}

	public handlePageEvent(event: PageEvent): void {
		if (event.pageIndex !== event.previousPageIndex) {
			this.selectedPageIndex = event.pageIndex - 1;
		}
	}
}
